import { useMemo } from 'react'

import {
  buildCategoryStatRowItems,
  StatRowsCardView,
  type CategoryDefinition,
} from 'src/pages/AnalyticsPage/components/StatRowsCard'

/**
 * Ordered list of call statuses + their accent colors. Order drives the
 * visual order of the rows; colors are pastel mid-tones reused from the
 * previous CallSparklineCard so the visual semantics carry over.
 *
 * Exported so callers that aggregate the daily metrics into this summary
 * shape (e.g. `generateMetricsByDateRange`) can read their status keys
 * from the same source — otherwise a typo or drift between the two lists
 * silently zeroes out a row.
 */
export const CALL_STATUS_CATEGORIES: ReadonlyArray<CategoryDefinition> = [
  { id: 'Completed', label: 'Completed', color: '#75E0A7' },
  { id: 'No Answer', label: 'No Answer', color: '#77BEE7' },
  { id: 'Busy', label: 'Busy', color: '#D0D5DD' },
  { id: 'Left Voicemail', label: 'Left Voicemail', color: '#FEC84B' },
  { id: 'Failed', label: 'Failed', color: '#FDA29B' },
]

/** Status labels in display order — the keys produced by upstream metric
 * aggregation must use these exact strings. */
export const CALL_STATUS_LABELS = CALL_STATUS_CATEGORIES.map((c) => c.label)

interface CallStatusMetricRow {
  /** Date bucket — present in the source data but unused for summary totals. */
  date?: string
  /**
   * The remaining keys are the per-status counts. The values mirror the
   * label of each category (e.g. row["Completed"] is the count of
   * completed calls in this date bucket).
   */
  [statusLabel: string]: number | string | undefined
}

export interface CallStatusSummaryCardProps {
  /** Per-day metrics shaped by `generateMetricsByDateRange`. */
  dailyMetrics: ReadonlyArray<CallStatusMetricRow>
  loading?: boolean
}

const INFO_TEXT =
  'Counts of calls in the selected date range, grouped by their final status. Each row shows the total and what share of the period it represents.'

export const CallStatusSummaryCard = ({
  dailyMetrics,
  loading = false,
}: CallStatusSummaryCardProps) => {
  const counts = useMemo(() => {
    const out: Record<string, number> = {}
    for (const category of CALL_STATUS_CATEGORIES) {
      out[category.id] = dailyMetrics.reduce(
        (acc, row) => acc + ((row[category.label] as number | undefined) ?? 0),
        0
      )
    }
    return out
  }, [dailyMetrics])

  const items = useMemo(
    () => buildCategoryStatRowItems(CALL_STATUS_CATEGORIES, counts),
    [counts]
  )

  return (
    <StatRowsCardView
      title="Call Status Summary"
      info={INFO_TEXT}
      items={items}
      loading={loading}
      pctSuffix="of period"
      emptySubtext="No calls"
    />
  )
}

export default CallStatusSummaryCard
